import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Compass, HeartHandshake, Target, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { images } from '../mock';

const values = [
  { icon: Compass, title: 'Practitioner-led', body: 'Built by South African accountants and auditors who lived the pain of scattered spreadsheets.' },
  { icon: HeartHandshake, title: 'Partner, not vendor', body: 'We win when your practice wins. Our team stays close throughout onboarding and beyond.' },
  { icon: Target, title: 'Compliance-first', body: 'Every workflow is mapped to SARS, CIPC and POPIA — so you never chase a deadline again.' },
  { icon: Users, title: 'People-shaped software', body: 'We design for real firms, real trainees and real partners — not for demos.' },
];

export default function About() {
  return (
    <div className="bg-[#fdfaf3]">
      <section className="pt-14 pb-16 lg:pt-20 lg:pb-24 border-b border-stone-200 bg-gradient-to-b from-[#f4efe4] to-[#fdfaf3]">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-xs tracking-widest font-semibold text-emerald-800">ABOUT ACCU DESK</div>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-stone-900 leading-[1.05]">A practice management platform, shaped by South African firms.</h1>
          <p className="mt-6 text-lg text-stone-700 max-w-2xl mx-auto leading-relaxed">Accu Desk, powered by Saber Accounting, was born inside a busy Johannesburg practice. We built the tool we wished we had — then opened it up to every firm that has ever missed a submission or lost a night to reconciliations.</p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs tracking-widest font-semibold text-emerald-800">OUR STORY</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-serif font-semibold text-stone-900 leading-tight">From a spreadsheet nightmare to a nationwide platform.</h2>
            <p className="mt-5 text-stone-700 leading-relaxed">In 2019, our founding partners were juggling a 480-tab deadline spreadsheet, three billing tools and a shared inbox. After one missed provisional deadline too many, they started building a single system — one that would talk to SARS, sync to Sage and Xero, and keep every trainee, manager and partner aligned.</p>
            <p className="mt-4 text-stone-700 leading-relaxed">Today, Accu Desk serves 500+ practices from Cape Town to Windhoek, saving each user roughly 14 hours a week. We are proudly SA-built, POPIA-aligned and local-support all the way.</p>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-emerald-100/60 blur-2xl"/>
            <img src={images.team} alt="Team" className="relative rounded-2xl shadow-xl ring-1 ring-stone-900/5 w-full object-cover"/>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#fdfaf3] border-t border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-widest font-semibold text-emerald-800">WHAT WE BELIEVE</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-serif font-semibold text-stone-900 max-w-3xl leading-tight">Values that guide every feature we ship.</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="rounded-2xl bg-white border border-stone-200 p-6 hover:shadow-lg transition-shadow">
                <div className="h-11 w-11 rounded-lg bg-emerald-50 text-emerald-800 grid place-items-center">
                  <v.icon className="h-5 w-5"/>
                </div>
                <div className="mt-4 font-semibold text-stone-900">{v.title}</div>
                <div className="mt-2 text-sm text-stone-600 leading-relaxed">{v.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-emerald-900 text-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <Award className="h-10 w-10 text-emerald-300 mx-auto"/>
          <h2 className="mt-4 text-3xl sm:text-4xl font-serif font-semibold">Ready to run a calmer practice?</h2>
          <p className="mt-4 text-emerald-100 max-w-xl mx-auto">Start a 30-day trial or chat to our Johannesburg team about a hands-on onboarding.</p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/signup"><Button className="bg-white text-emerald-900 hover:bg-emerald-50 h-12 px-6">Start Free Trial <ArrowRight size={16} className="ml-2"/></Button></Link>
            <Link to="/contact"><Button variant="outline" className="border-emerald-300 text-white hover:bg-emerald-800 h-12 px-6 bg-transparent">Talk to sales</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
